import { Box, Divider, HStack, VStack } from '@chakra-ui/react';
import { MinusIcon } from 'Tokens/Icons/Data.tsx';
import { CheckIcon, LoaderIcon } from 'Tokens/Icons/Status.tsx';
import { Typography } from 'Tokens/index.ts';
import { TaskListAssessmentFilter } from './TaskListAssessmentFilter';
import { TaskOwnerFilter } from './TaskOwnerFilter';
import { TaskSortingMenu } from './TaskSortingMenu';
import { TaskSearchBar } from './TaskSearchBar';
import { TaskProgressCircle } from './TaskProgressCircle';
import { IconButton } from 'Atoms/index.ts';
import { SearchIcon } from 'Tokens/Icons/Function';
import React, { useState } from 'react';
import { SelectedAssessmentOption } from './TaskManagement.tsx';
import { TaskCompanyOption, TaskListSortBy } from '../hooks/TaskList.hooks.ts';
import {
  TaskFilter,
  TaskFilterOptions,
} from 'containers/Esrs/pieces/DisclosureRequirements/Metrics/MetricsTable/MetricsTable.utils.tsx';

interface TaskListFilterBarProps {
  selectedAssessment?: SelectedAssessmentOption;
  setSelectedAssessment: (assessment: SelectedAssessmentOption) => void;
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  sortBy: TaskListSortBy;
  taskFilterOption?: TaskFilterOptions;
  setSortBy: (sort: TaskListSortBy) => void;
  selectedUserId?: string;
  setSelectedUserId?: (val: string) => void;
  setTaskFilter?: (val: TaskFilterOptions) => void;
  sortDirection?: 'asc' | 'desc';
  setSortDirection?: (dir: 'asc' | 'desc') => void;
  completed?: number;
  total?: number;
  searchOpen?: boolean;
  setSearchOpen?: (val: boolean) => void;
  companyOptions: TaskCompanyOption[];
  isHomePage?: boolean;
}

export const TaskListFilterBar = ({
  selectedAssessment,
  setSelectedAssessment,
  taskFilterOption,
  searchQuery,
  setSearchQuery,
  sortBy,
  setSortBy,
  sortDirection,
  selectedUserId,
  setSelectedUserId,
  setTaskFilter,
  setSortDirection,
  searchOpen,
  setSearchOpen,
  completed,
  total,
  companyOptions,
  isHomePage,
}: TaskListFilterBarProps) => {
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const handleSearchBlur = () => {
    setTimeout(() => {
      if (!searchQuery) setSearchOpen?.(false);
    }, 100);
  };

  const hasTaskFilter = !!taskFilterOption && !!setTaskFilter && !!setSelectedUserId;
  const hasProgressCircle = !!completed && !!total;
  const hasSearch = !!searchQuery && !!setSearchQuery;

  return (
    <HStack width="100%" maxW="1200px" mx="auto" spacing={4} align="center" justify="space-between">
      <HStack spacing="8px" align="center" justify="start">
        {hasProgressCircle && <TaskProgressCircle completed={completed} total={total} />}
        <TaskListAssessmentFilter
          selectedAssessment={selectedAssessment}
          setSelectedAssessment={setSelectedAssessment}
          companyOptions={companyOptions}
          isHomePage={isHomePage}
        />
      </HStack>
      <HStack>
        {hasTaskFilter && (
          <TaskFilter
            taskFilterOption={taskFilterOption}
            selectedUserId={selectedUserId ?? ''}
            setSelectedUserId={setSelectedUserId}
            setTaskFilter={setTaskFilter}
            withLeftIcon
          />
        )}
        <TaskSortingMenu
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          isHomepage={isHomePage}
        />
        <Divider
          orientation="vertical"
          height="32px"
          borderColor="gray.200"
          display={isHomePage ? 'none' : 'block'}
        />
        <Box
          position="relative"
          width={searchOpen ? '200px' : '0px'}
          transition="width 0.25s cubic-bezier(0.4,0,0.2,1)"
          overflow="hidden"
          display={isHomePage ? 'none' : 'flex'}
          alignItems="center"
        >
          {hasSearch && searchOpen && (
            <TaskSearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              // @ts-ignore
              inputRef={searchInputRef}
              onBlur={handleSearchBlur}
            />
          )}
        </Box>
        {!searchOpen && (
          <IconButton
            aria-label="Search"
            icon={<SearchIcon />}
            variant="ghost"
            size="sm"
            onClick={() => setSearchOpen?.(true)}
            display={isHomePage ? 'none' : 'block'}
          />
        )}
      </HStack>
    </HStack>
  );
};

export const TaskListProgressBar = ({
  completed,
  inProgress,
  notStarted,
  total,
}: {
  completed: number;
  inProgress: number;
  notStarted: number;
  total: number;
}) => {
  const completedPct = total > 0 ? (completed / total) * 100 : 0;
  const inProgressPct = total > 0 ? (inProgress / total) * 100 : 0;
  const notStartedPct = total > 0 ? (notStarted / total) * 100 : 0;

  return (
    <HStack width="100%" spacing="0px" borderRadius="5px" overflow="hidden">
      {completedPct && (
        <HStack
          width={`${completedPct}%`}
          minWidth={completed > 0 ? '130px' : '0px'}
          bgColor="bg.success"
          spacing="2px"
          pl="8px"
          paddingY="2px"
          h="28px"
          borderStartRadius="5px"
        >
          <CheckIcon color="text.success" paddingLeft="2px" />
          <Typography variant="detailStrong" color="text.success">
            {completed} Completed
          </Typography>
        </HStack>
      )}
      {inProgressPct && (
        <HStack
          width={`${inProgressPct}%`}
          minWidth={inProgress > 0 ? '130px' : '0px'}
          bgColor="bg.info"
          spacing="2px"
          pl="8px"
          paddingY="2px"
          h="28px"
        >
          <LoaderIcon color="text.info" paddingLeft="2px" />
          <Typography color="text.info" variant="detailStrong">
            {inProgress} In progress
          </Typography>
        </HStack>
      )}
      {notStartedPct && (
        <HStack
          width={`${notStartedPct}%`}
          minWidth={notStarted > 0 ? '130px' : '0px'}
          paddingY="2px"
          bg="bg.unknown"
          pl="8px"
          spacing="2px"
          borderEndRadius="5px"
          h="28px"
        >
          <MinusIcon color="text.muted" paddingLeft="2px" />
          <Typography color="text.muted" variant="detailStrong">
            {notStarted} Not started
          </Typography>
        </HStack>
      )}
    </HStack>
  );
};

export const TaskListHeader = () => {
  return (
    <VStack w={'100%'} align="start" spacing={0} paddingTop="24px" paddingBottom="16px">
      <Typography variant="h1">Task management (beta)</Typography>
      <Typography variant="body">Manage and track your tasks to completion</Typography>
    </VStack>
  );
};
